import ErrorOutline from '@material-ui/icons/ErrorOutline'
import {useEvent} from 'Event/EventProvider'
import {ObieAttendeeLink} from 'lib/event-api/integrations/obie/attendee-links/list'
import {
  ObieJourney,
  useObieJourneys,
} from 'lib/event-api/integrations/obie/journeys/list'
import {
  ObieMilestone,
  useObieMilestones,
} from 'lib/event-api/integrations/obie/milestones/list'
import {useObieSteps} from 'lib/event-api/integrations/obie/steps/list'
import {useOrganization} from 'organization/OrganizationProvider'
import React from 'react'
import styled from 'styled-components'

export interface AttendeeLinkDestinationProps {
  link: ObieAttendeeLink
  obieHubId: string
}

export default function AttendeeLinkDestination(
  props: AttendeeLinkDestinationProps,
) {
  const {link, obieHubId} = props
  const {event} = useEvent()
  const {client} = useOrganization()
  const {data: journeys, isLoading, error} = useObieJourneys({
    client,
    event,
    obieHubId,
  })

  if (isLoading) {
    return <Muted>Loading…</Muted>
  }

  if (error) {
    return <Flag message={error.message} />
  }

  const journey = journeys?.find((item) => item.id === link.obie_journey_id)
  if (!journey) {
    return <Flag message="Not in this AI Hub" />
  }

  if (!link.obie_milestone_id) {
    return <span>Journey · {journey.name}</span>
  }

  return (
    <MilestoneDestination
      journey={journey}
      obieMilestoneId={link.obie_milestone_id}
      obieStepId={link.obie_step_id}
    />
  )
}

interface MilestoneDestinationProps {
  journey: ObieJourney
  obieMilestoneId: string
  obieStepId: string | null
}

function MilestoneDestination(props: MilestoneDestinationProps) {
  const {journey, obieMilestoneId, obieStepId} = props
  const {event} = useEvent()
  const {client} = useOrganization()
  const {data: milestones, isLoading, error} = useObieMilestones({
    client,
    event,
    obieJourneyId: journey.id,
  })

  if (isLoading) {
    return <Muted>Loading…</Muted>
  }

  if (error && error.status !== 404) {
    return <Flag message={error.message} />
  }

  const milestone = milestones?.find((item) => item.id === obieMilestoneId)
  if (!milestone) {
    return <Flag message="No longer available" />
  }

  if (!obieStepId) {
    return (
      <span>
        Milestone · {journey.name} › {milestone.name}
      </span>
    )
  }

  return (
    <StepDestination
      journey={journey}
      milestone={milestone}
      obieStepId={obieStepId}
    />
  )
}

interface StepDestinationProps {
  journey: ObieJourney
  milestone: ObieMilestone
  obieStepId: string
}

function StepDestination(props: StepDestinationProps) {
  const {journey, milestone, obieStepId} = props
  const {event} = useEvent()
  const {client} = useOrganization()
  const {data: steps, isLoading, error} = useObieSteps({
    client,
    event,
    obieMilestoneId: milestone.id,
  })

  if (isLoading) {
    return <Muted>Loading…</Muted>
  }

  if (error && error.status !== 404) {
    return <Flag message={error.message} />
  }

  const step = steps?.find((item) => item.id === obieStepId)
  if (!step) {
    return <Flag message="No longer available" />
  }

  return (
    <span>
      Step · {journey.name} › {milestone.name} › {step.name}
    </span>
  )
}

function Flag(props: {message: string}) {
  const {message} = props

  return (
    <FlagText>
      <ErrorOutline fontSize="small" />
      {message}
    </FlagText>
  )
}

const Muted = styled.span`
  color: ${(props) => props.theme.colors.text.muted};
`

const FlagText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[1]};
  color: ${(props) => props.theme.colors.error};
`
